import React from 'react';
import {
  BrowserRouter, Routes, Route, Navigate, Link, useLocation,
} from 'react-router-dom';
import { FileSignature, Trash2, User, LogOut, Home } from 'lucide-react';
import '../../dm.css';
import DMEnvelopeList from './DMEnvelopeList';
import DMEnvelopeDetail from './DMEnvelopeDetail';
import DMTrashList from './DMTrashList';

function DMSidebar({ user, onSignOut }) {
  const location = useLocation();
  const path = location.pathname;
  const envelopesActive = path === '/dm' || path.startsWith('/dm/envelopes');
  const trashActive = path.startsWith('/dm/trash');

  return (
    <aside className="dm-sidebar">
      <div className="dm-sidebar__brand">
        <img src="/cagan-logo.png" alt="Cagan Logo" className="dm-sidebar__logo-img" />
        <div className="dm-sidebar__brand-text">
          <span className="dm-sidebar__brand-title">Cagan</span>
          <span className="dm-sidebar__brand-subtitle">Document Manager</span>
        </div>
      </div>

      <nav className="dm-sidebar__nav">
        <Link
          to="/dm/envelopes"
          className={`dm-sidebar__link ${envelopesActive ? 'dm-sidebar__link--active' : ''}`}
        >
          <FileSignature size={18} />
          Envelopes
        </Link>
        <Link
          to="/dm/trash"
          className={`dm-sidebar__link ${trashActive ? 'dm-sidebar__link--active' : ''}`}
        >
          <Trash2 size={18} />
          Trash
        </Link>
      </nav>

      {user?.hasCRMAccess !== false && (
        <div className="crm-sidebar__section">
          <div className="crm-sidebar__section-label">Apps</div>
          <button
            type="button"
            className="dm-sidebar__link"
            onClick={() => { window.location.href = '/'; }}
          >
            <Home size={18} />
            CRM
          </button>
        </div>
      )}

      <div className="dm-sidebar__footer">
        <div className="dm-sidebar__user">
          <div className="dm-sidebar__user-avatar">
            <User size={16} />
          </div>
          <div className="dm-sidebar__user-info">
            <span className="dm-sidebar__user-name">{user?.name || 'Signed in'}</span>
          </div>
          <button className="crm-sidebar__logout" onClick={onSignOut} data-tooltip="Sign out">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}

/**
 * DMShell — standalone Document Manager app shell. Self-contained: it owns
 * its own BrowserRouter and sub-routes and does NOT reuse the CRM shell.
 * Rendered by AuthGate for users routed to the Document Manager.
 *
 * Props:
 *   user      — { name, ... } current user (from AuthGate)
 *   onSignOut — () => void     (AuthGate's logout)
 */
function DMShell({ user, onSignOut }) {
  return (
    <BrowserRouter>
      <div className="dm-shell">
        <DMSidebar user={user} onSignOut={onSignOut} />
        <main className="dm-main">
          <div className="dm-content">
            <Routes>
              <Route path="/" element={<Navigate to="/dm/envelopes" replace />} />
              <Route path="/dm" element={<Navigate to="/dm/envelopes" replace />} />
              <Route path="/dm/envelopes" element={<DMEnvelopeList />} />
              <Route path="/dm/envelopes/new" element={<DMEnvelopeList createMode />} />
              <Route path="/dm/envelopes/:id" element={<DMEnvelopeDetail />} />
              <Route path="/dm/trash" element={<DMTrashList />} />
              <Route path="*" element={<Navigate to="/dm/envelopes" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </BrowserRouter>
  );
}

export default DMShell;
